import HomeOutlined from "@ant-design/icons/HomeOutlined";
import LockOutlined from "@ant-design/icons/LockOutlined";

import { getRoleFromPermission } from "@sdk/roles-and-permissions/roles-and-permissions";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Layout,
  message,
  Typography,
} from "antd";
import classNames from "classnames";
import { Avatar } from "components/common/avatar/avatar";
import { push } from "connected-react-router";
import { useCallback, useMemo } from "react";
import { useSelector, useStore } from "react-redux";
import {
  addRecentLoginData,
  removeRecentLoginData,
  setSwitchingProfile,
} from "store/modules/app-state/app-state.actions";
import { iRecentLoginData } from "store/modules/app-state/app-state.model";
import { selectRecentLogins } from "store/modules/app-state/app-state.selectors";
import { setPreviousDomain } from "store/modules/preferences/preferences.actions";
import { selectPreviousDomain } from "store/modules/preferences/preferences.selectors";
import { UserTracker } from "user-tracker";
import { justWait } from "utils/just-wait";
import { useSDKActionWithDeps } from "../../../@sdk/sdk.hooks";
import {
  logout,
  setAccessToken,
  setCurrentUser,
} from "../../../store/modules/users/users.actions";
import "./login.page.style.scss";

const { Footer } = Layout;
const { Title } = Typography;

const LoginPage = () => {
  const store = useStore();

  const goToDashboard = useCallback(() => store.dispatch(push("/")), [store]);
  const goToRegister = useCallback(
    () => store.dispatch(push("/register")),
    [store],
  );
  const goToResetPassword = useCallback(
    () => store.dispatch(push("/reset-password")),
    [store],
  );

  const recentLogins = useSelector(selectRecentLogins);
  const {
    doAction: onFinish,
    isProcessing,
    response,
    dispatch,
  } = useSDKActionWithDeps(
    () => ({
      action:
        (SDK) =>
        (values: { domain: string; email: string; password: string }) =>
          SDK.login(
            values.domain.toLowerCase(),
            values.email.toLowerCase(),
            values.password,
          )
            .then((res) => {
              const { user, access } = res;
              // Validate IP
              const role = getRoleFromPermission(user.permissions);
              if (role !== "Owner") {
                if (!access.valid) {
                  dispatch(logout());
                  throw {
                    response: {
                      data: {
                        message:
                          "Your IP has been blocked from accessing portal",
                      },
                    },
                  };
                }
              }
              return res;
            })
            .then((res) => {
              const enableSubDomains =
                !window.location.href.includes("localhost") &&
                !window.location.href.includes("vercel.app");
              if (enableSubDomains) {
                window.location.href = `https://${values.domain.toLowerCase()}.clickconnector.io/login?accessToken=${
                  res.accessToken
                }`;
                return justWait(4000);
              } else {
                SDK.configure({ token: res.accessToken });
                store.dispatch(setPreviousDomain(values.domain));
                dispatch(setCurrentUser(res.user));
                dispatch(setAccessToken(res.accessToken));
                dispatch(
                  addRecentLoginData({
                    // Org Data
                    domain: values.domain.toLowerCase(),
                    // organizationLogo: string,
                    // User Data
                    email: values.email.toLowerCase(),
                    firstName: res.user.data.firstName,
                    lastName: res.user.data.lastName,
                    avatar: res.user.data.avatar,
                    // Access Data
                    accessToken: res.accessToken,
                  }),
                );
              }
              return res;
            }),
      successMessage: "Successfully logged in",
      failureMessage: "Something went wrong",
    }),
    [store],
  );

  const lastDomain = useSelector(selectPreviousDomain);

  const switchProfile = useCallback(
    (profileData: iRecentLoginData) => {
      if (
        window.location.href.includes(
          `https://${profileData.domain.toLowerCase()}.clickconnector.io`,
        ) ||
        window.location.href.includes("localhost")
      ) {
        dispatch(setSwitchingProfile(true));
        message.info("Switching Profile");
        dispatch(logout());
        setTimeout(() => {
          dispatch(setAccessToken(profileData.accessToken));
          dispatch(setSwitchingProfile(false));
        }, 100);
        UserTracker.track("Swich Profile", {
          domain: profileData.domain,
          email: profileData.email,
        });
      } else {
        dispatch(setSwitchingProfile(false));
        window.location.href = `https://${profileData.domain.toLowerCase()}.clickconnector.io/login?accessToken=${
          profileData.accessToken
        }`;
      }
    },
    [dispatch],
  );

  const removeProfile = useCallback(
    (profileData: iRecentLoginData) => {
      dispatch(
        removeRecentLoginData({
          email: profileData.email,
          domain: profileData.domain,
        }),
      );
    },
    [dispatch],
  );

  const domainConfig = useMemo(() => {
    if (window.location.href.includes(".clickconnector.io")) {
      const domain = window.location.href
        .split(".clickconnector.io")[0]
        .replace("https://", "");
      if (domain === "desktop") {
        return {
          domain: "",
          editable: true,
        };
      }
      return {
        domain,
        editable: false,
      };
    }
    return {
      domain: "",
      editable: true,
    };
  }, []);

  const LoginForm = (
    <>
      <div className="text-gray-600 dark:text-gray-400 mode_transition">
        Let's get you authenticated
      </div>
      <Form
        name="normal_login"
        className="login-form"
        initialValues={{
          remember: true,
          domain: lastDomain || domainConfig.domain || "",
        }}
        onFinish={onFinish}
      >
        <Form.Item
          name="domain"
          rules={[
            { required: true, message: "Please enter your workspace domain" },
          ]}
        >
          <Input
            prefix={<HomeOutlined className="site-form-item-icon" />}
            placeholder="Domain"
            size="large"
            addonAfter=".clickconnector.io"
            disabled={!domainConfig.editable}
          />
        </Form.Item>

        <Divider></Divider>

        <Form.Item
          name="email"
          rules={[{ required: true, message: "Please input your Email!" }]}
        >
          <Input
            prefix={<i className="ri-user-line"></i>}
            placeholder="Email"
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[{ required: true, message: "Please input your Password!" }]}
        >
          <Input
            prefix={<LockOutlined className="site-form-item-icon" />}
            type="password"
            placeholder="Password"
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox>Remember me</Checkbox>
          </Form.Item>

          <Button
            type="text"
            className="login-form-forgot"
            onClick={() => goToResetPassword()}
          >
            Forgot password
          </Button>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="login-form-button"
            loading={isProcessing}
            size="large"
          >
            Log in
          </Button>
          {/* Or{" "}
    <Button type="link" onClick={() => props.goToRegister()}>
      Register now!
    </Button> */}
        </Form.Item>
      </Form>
    </>
  );

  if (recentLogins && recentLogins.length > 0) {
    return (
      <Layout className="layout w-full flex flex-col justify-center items-center  min-h-full sm:flex-row ">
        <div
          className="left-side-section flex flex-col p-12 mode_transition bg-white dark:bg-gray-900 rounded-l-2xl"
          style={{ width: 420, height: 600 }}
        >
          <img
            src="/cc-assets/logo.png"
            className="sm:my-8"
            style={{ width: "10rem" }}
            alt="ClickConnector"
          />
          <Title level={3}>Welcome Back</Title>
          <div className="text-gray-600 dark:text-gray-400 mode_transition mb-8">
            Select a profile to continue
          </div>
          <div style={{ height: 400 }} className="overflow-auto">
            {recentLogins.length > 0 &&
              recentLogins.map((item, index) => (
                <div
                  key={index.toString()}
                  onClick={() => switchProfile(item)}
                  className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 p-2 recent-account-item"
                >
                  <div className="flex flex-row items-center">
                    <Avatar
                      avatarUrl={item.avatar}
                      name={item.firstName}
                      size={48}
                    />
                    <div className={classNames("flex flex-col pl-2 flex-1")}>
                      <div className="email">{item.domain}</div>
                      <div className="domain text-gray-600"> {item.email}</div>
                    </div>
                    <div
                      className="delete-button"
                      onClick={(e) => {
                        removeProfile(item);
                        e.stopPropagation();
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <Divider className="md:hidden bg-white m-0" style={{}}>
            Or
          </Divider>
        </div>
        <div
          className="flex-col justify-between items-stretch bg-white dark:bg-gray-900  md:flex hidden"
          style={{ height: 600 }}
        >
          <Divider
            type="vertical"
            orientation="center"
            style={{ height: 280 }}
          ></Divider>
          <div className="text flex-1 flex justify-center items-center text-center">
            Or
          </div>
          <Divider
            type="vertical"
            orientation="center"
            style={{ height: 280 }}
          ></Divider>
        </div>

        <div
          className="sm:my-8 bg-white dark:bg-gray-900 p-12 bold-form-labels mode_transition flex flex-col justify-center rounded-r-2xl"
          style={{ height: 600, width: 420 }}
        >
          {LoginForm}
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="layout h-full w-full flex flex-col justify-center items-center">
      <img
        src="/cc-assets/logo.png"
        style={{ width: "5rem" }}
        alt="ClickConnector"
      />
      <div className="mt-4 bg-white dark:bg-gray-900 p-12 rounded-2xl  mode_transition">
        <Title level={3}>Welcome Back</Title>
        {LoginForm}
      </div>
      <Footer style={{ textAlign: "center" }}>
        ClickConnector ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
};

export default LoginPage;
